import type { PlayerStore } from '../../state/player_store.js';
import type { SubscribersList } from '../../state/subscribers_list.js';
import type { Subtitle } from '../../types.js';

import styles from '../player.module.css';
import subtitleStyles from './subtitles.module.css';

export class SubtitleHandler {
  #mediaElement: HTMLMediaElement;
  #subtitlesContainer: HTMLDivElement = document.createElement('div');
  #subtitlesBackdrop: HTMLDivElement = document.createElement('div');
  #store: PlayerStore;
  #useCustomRendering: boolean = true;

  constructor(
    mediaElement: HTMLMediaElement,
    container: HTMLElement,
    subscribersList: SubscribersList,
    store: PlayerStore,
  ) {
    this.#mediaElement = mediaElement;
    this.#store = store;
    subscribersList.listen(
      store.fullscreen.$fullscreenActive,
      this.#handleFullscreen,
    );
    subscribersList.subscribe(
      store.fullscreen.$fullscreenControlsExpanded,
      this.#handleFullscreen,
    );
    this.#subtitlesContainer.classList.add(
      styles.player,
      subtitleStyles.container,
    );
    this.#subtitlesBackdrop.classList.add(subtitleStyles.backdrop);
    this.#subtitlesContainer.appendChild(this.#subtitlesBackdrop);
    container.appendChild(this.#subtitlesContainer);
    if (this.#useCustomRendering) {
      this.#mediaElement.textTracks.addEventListener(
        'change',
        this.setupSubtitleListener,
      );
    }
  }

  setupSubtitles = (subtitles: Subtitle | undefined) => {
    if (!subtitles) {
      this.#clearTracks();
      return;
    }

    this.#clearTracks();
    this.#addTrack(subtitles);
  };

  #handleFullscreen = () => {
    const fullscreenActive = this.#store.fullscreen.$fullscreenActive.get();
    const fullscreenControlsExpanded =
      this.#store.fullscreen.$fullscreenControlsExpanded.get();
    const shouldOffsetSubtitles =
      fullscreenActive && fullscreenControlsExpanded;
    this.#subtitlesContainer.style.setProperty(
      '--subtitles-offset',
      shouldOffsetSubtitles ? '100px' : '',
    );
  };

  // VHS (Video.js HTTP Streaming) creates internal TextTracks with kind='metadata'
  // to hold per-segment info (bandwidth, resolution, etc.) when running in MSE mode
  // (overrideNative:true). In Safari, VHS uses VTTCue for those metadata cues, which
  // pass an `instanceof VTTCue` check and would be rendered as subtitle text.
  // Filtering to subtitles/captions tracks only prevents that from happening.
  #findSubtitleTrack(): TextTrack | undefined {
    const { textTracks } = this.#mediaElement;
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i];
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        return track;
      }
    }
    return undefined;
  }

  #handleCueChange = () => {
    const textTrack = this.#findSubtitleTrack();
    if (!textTrack) {
      this.#subtitlesBackdrop.textContent = '';
      return;
    }
    textTrack.mode = this.#useCustomRendering ? 'hidden' : 'showing';

    const [firstCue] = textTrack.activeCues ?? [];
    if (firstCue instanceof VTTCue) {
      this.#subtitlesBackdrop.innerHTML = '';
      this.#subtitlesBackdrop.appendChild(firstCue.getCueAsHTML());
    } else {
      this.#subtitlesBackdrop.textContent = '';
    }
  };

  #clearTracks = () => {
    // Disable the subtitle track if present — without this Firefox keeps the
    // last subtitle visible on screen even after the track element is removed.
    const textTrack = this.#findSubtitleTrack();
    if (textTrack) {
      textTrack.mode = 'disabled';
    }

    // Remove existing track elements,
    for (const track of this.#mediaElement.querySelectorAll('track')) {
      track.remove();
    }
  };

  #addTrack = (subtitles: Subtitle) => {
    const { id, url, name, language } = subtitles;
    const track = document.createElement('track');
    Object.assign(track, {
      id,
      src: url,
      kind: 'subtitles',
      label: name,
      srclang: language,
      default: true,
    });
    this.#mediaElement.append(track);
    const addedTrack = this.#findSubtitleTrack();
    if (addedTrack) {
      addedTrack.mode = this.#useCustomRendering ? 'hidden' : 'showing';
    }
  };

  setupSubtitleListener = () => {
    const textTrack = this.#findSubtitleTrack();
    this.#handleCueChange();
    if (!textTrack) {
      return;
    }

    textTrack.addEventListener('cuechange', this.#handleCueChange);
  };

  destroy = () => {
    const textTrack = this.#findSubtitleTrack();
    this.#mediaElement.textTracks.removeEventListener(
      'change',
      this.setupSubtitleListener,
    );
    if (textTrack) {
      textTrack.removeEventListener('cuechange', this.#handleCueChange);
    }
    this.#subtitlesContainer.remove();
  };
}
